import { useCallback } from 'react';

import { defineMessages, FormattedMessage, useIntl } from 'react-intl';

import { PencilSimpleIcon, MinusIcon, XIcon } from '@phosphor-icons/react';

import { IconButton } from '@/flavours/glitch/components/button/redesign';
import {
  closeComposer,
  minimizeComposerToggle,
  selectIsMinimized,
} from '@/flavours/glitch/reducers/slices/composer';
import {
  createAppSelector,
  useAppDispatch,
  useAppSelector,
} from '@/flavours/glitch/store';

import { selectComposeType } from './selectors';
import classes from './styles.module.scss';

const messages = defineMessages({
  postNew: {
    id: 'compose.post.title.new',
    defaultMessage: 'New post',
  },
  postEdit: {
    id: 'compose.post.title.edit',
    defaultMessage: 'Edit post',
  },
  replyNew: {
    id: 'compose.reply.title.new',
    defaultMessage: 'New reply',
  },
  replyEdit: {
    id: 'compose.reply.title.edit',
    defaultMessage: 'Edit reply',
  },
  replyPrivateNew: {
    id: 'compose.reply_private.title.new',
    defaultMessage: 'New private reply',
  },
  replyPrivateEdit: {
    id: 'compose.reply_private.title.edit',
    defaultMessage: 'Edit private reply',
  },
  messageNew: {
    id: 'compose_form.message.title.new',
    defaultMessage: 'New message',
    description:
      'Message refers to a direct message. For languages where this is confusing, "chat" or "direct message" can be used.',
  },
  messageEdit: {
    id: 'compose_form.message.title.edit',
    defaultMessage: 'Edit message',
    description:
      'Message refers to a direct message. For languages where this is confusing, "chat" or "direct message" can be used.',
  },
});

const selectComposeFormTitle = createAppSelector(
  [selectComposeType, (state) => state.compose.get('id') as null | string],
  (type, id) => {
    return messages[`${type}${id ? 'Edit' : 'New'}`];
  },
);

export const ComposeFormHeader: React.FC<{
  id?: string;
  noMinimize?: boolean;
}> = ({ id, noMinimize }) => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const titleMessage = useAppSelector(selectComposeFormTitle);
  const isMinimized = useAppSelector(selectIsMinimized);

  const onClose = useCallback(() => {
    dispatch(closeComposer());
  }, [dispatch]);

  const onMinimize = useCallback(() => {
    dispatch(minimizeComposerToggle());
  }, [dispatch]);

  return (
    <header className={classes.header}>
      <h2 id={id}>{intl.formatMessage(titleMessage)}</h2>

      {!noMinimize && (
        <IconButton
          size='sm'
          variant='ghost'
          icon={isMinimized ? PencilSimpleIcon : MinusIcon}
          onClick={onMinimize}
          tooltipPlacement='top'
        >
          {isMinimized ? (
            <FormattedMessage
              id='compose.resume_draft'
              defaultMessage='Resume draft'
            />
          ) : (
            <FormattedMessage
              id='compose.minimize'
              defaultMessage='Minimize composer'
            />
          )}
        </IconButton>
      )}

      <IconButton
        icon={XIcon}
        variant='ghost'
        size='sm'
        onClick={onClose}
        tooltipPlacement='top'
      >
        <FormattedMessage id='lightbox.close' defaultMessage='Close' />
      </IconButton>
    </header>
  );
};
